/**
 * 三种输入归成规则核心的一个「想往哪走」（ArcadeInput）：
 * - 摇杆（触屏左下角）：方向与力度；
 * - 方向键或 WASD：方向，斜着按两个键时长度仍是 1；
 * - 在场地上点或拖：目标点，宠物走过去、到了停下（松手后仍朝最后那个点走）。
 * 同时有好几种时，摇杆优先，其次按键，最后是目标点。
 */
import type { ArcadeInput } from "../core";

const DIRECTIONS: Record<string, readonly [number, number]> = {
  ArrowUp: [0, -1], KeyW: [0, -1],
  ArrowDown: [0, 1], KeyS: [0, 1],
  ArrowLeft: [-1, 0], KeyA: [-1, 0],
  ArrowRight: [1, 0], KeyD: [1, 0],
};

/** 摇杆推到底之前，长度小于这个当没推（和规则核心的 0.05 一致） */
export const STICK_DEAD_ZONE = 0.05;

export function isMoveKey(code: string): boolean {
  return code in DIRECTIONS;
}

/** 此刻按着的方向键合成一个方向；没按或互相抵消为 null。 */
export function keyboardStick(pressed: ReadonlySet<string>): { dx: number; dy: number } | null {
  let dx = 0;
  let dy = 0;
  const seen = new Set<string>();
  for (const code of pressed) {
    const dir = DIRECTIONS[code];
    if (!dir) continue;
    // 方向键与 WASD 同一个方向只算一次
    const key = `${dir[0]},${dir[1]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    dx += dir[0];
    dy += dir[1];
  }
  const length = Math.hypot(dx, dy);
  return length === 0 ? null : { dx: dx / length, dy: dy / length };
}

/** 摇杆：手指离摇杆中心的偏移（CSS 像素）按摇杆半径换成方向与力度，长度截到 1。 */
export function stickFromDrag(offsetX: number, offsetY: number, radius: number): { dx: number; dy: number } {
  if (!(radius > 0)) return { dx: 0, dy: 0 };
  const dx = offsetX / radius;
  const dy = offsetY / radius;
  const length = Math.hypot(dx, dy);
  return length > 1 ? { dx: dx / length, dy: dy / length } : { dx, dy };
}

export function composeInput(sources: { stick: { dx: number; dy: number } | null; keys: ReadonlySet<string>; target: { x: number; y: number } | null }): ArcadeInput {
  const { stick, keys, target } = sources;
  if (stick && Math.hypot(stick.dx, stick.dy) >= STICK_DEAD_ZONE) return { kind: "stick", dx: stick.dx, dy: stick.dy };
  const fromKeys = keyboardStick(keys);
  if (fromKeys) return { kind: "stick", dx: fromKeys.dx, dy: fromKeys.dy };
  if (target) return { kind: "target", x: target.x, y: target.y };
  return { kind: "idle" };
}
